import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../assets/logo.png";
import signature from "../../assets/saam-signature.png";
import { calculateOfficeSchemeFee } from "./gstCalculator";

const COMPANY = {
  name: "SHINE AGRI ALLIED AND MARKETING MULTISTATE COOPERATIVE SOCIETY LTD.",
  sub: "Ministry Registered Multistate Cooperative Society",
  address: "Gujarat & Uttar Pradesh",
  gstin: "24ACNAS4114R1ZV",
  pan: "ACNAS4114R",
  bank: "INDUSIND BANK",
  accountName: "SHINE AGRI ALLIED AND MARKETING MSCS LTD.",
  accountNo: "201035786608",
  ifsc: "INDB0002348",
};

function inr(n) {
  return `Rs. ${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(Number(n) || 0)}`;
}

function words(num) {
  num = Math.round(Number(num) || 0);
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const w = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
    if (n < 1000) return a[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + w(n % 100) : "");
    if (n < 100000) return w(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + w(n % 1000) : "");
    if (n < 10000000) return w(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + w(n % 100000) : "");
    return w(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + w(n % 10000000) : "");
  };

  return num === 0 ? "Zero" : w(num);
}

export function generateGSTInvoicePDF(application) {
  const fee = calculateOfficeSchemeFee(application.estimateAmount);

  const invoiceNo = application.invoiceNo || "DRAFT-INVOICE";
  const invoiceDate =
    application.invoiceDate || new Date().toLocaleDateString("en-IN");

  const doc = new jsPDF("p", "mm", "a4");

  const navy = [20, 45, 85];
  const light = [243, 247, 252];

  // Page border
  doc.setDrawColor(...navy);
  doc.setLineWidth(0.6);
  doc.rect(8, 8, 194, 281);

  // Top header
  doc.addImage(logo, "PNG", 14, 14, 26, 26);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12.5);
  doc.setTextColor(...navy);
  doc.text(doc.splitTextToSize(COMPANY.name, 150), 44, 18);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(40, 40, 40);
  doc.text(COMPANY.sub, 44, 29);
  doc.text(COMPANY.address, 44, 34);
  doc.text(`GSTIN: ${COMPANY.gstin}    PAN: ${COMPANY.pan}`, 44, 39);

  // Invoice title band
  doc.setFillColor(...navy);
  doc.rect(8, 49, 194, 13, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("TAX INVOICE", 105, 58, { align: "center" });

  doc.setTextColor(0, 0, 0);

  // Invoice details table
  autoTable(doc, {
    startY: 70,
    margin: { left: 12, right: 12 },
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: 2.5,
      lineColor: [190, 200, 215],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: navy,
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    head: [["Invoice Details", "Bill To"]],
    body: [
      [
        `Invoice No: ${invoiceNo}\nInvoice Date: ${invoiceDate}\nPayment Mode: RTGS / NEFT`,
        `Society: ${application.societyName || "-"}\nReg. No: ${application.registrationNo || "-"}\nVillage/Taluka: ${application.village || "-"} / ${application.taluka || "-"}\nDistrict: ${application.district || "-"}\nMobile: ${application.mobile || "-"}`,
      ],
    ],
    columnStyles: {
      0: { cellWidth: 88 },
      1: { cellWidth: 98 },
    },
  });

  // Particulars table
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 8,
    margin: { left: 12, right: 12 },
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9.5,
      cellPadding: 3,
      lineColor: [180, 190, 205],
      lineWidth: 0.25,
    },
    headStyles: {
      fillColor: navy,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
    },
    bodyStyles: {
      textColor: [20, 20, 20],
    },
    alternateRowStyles: {
      fillColor: light,
    },
    head: [["Sr.", "Particulars", "SAC", "Taxable / Amount"]],
    body: [
      ["1", "Total Estimate Cost", "-", inr(fee.totalEstimate)],
      ["2", "Consultancy Fee @ 1.5% of Estimate Cost", "9983", inr(fee.consultancyFee)],
      ["3", "CGST @ 9%", "-", inr(fee.cgst)],
      ["4", "SGST @ 9%", "-", inr(fee.sgst)],
    ],
    foot: [["", "Grand Total Payable", "", inr(fee.grandTotal)]],
    footStyles: {
      fillColor: [230, 236, 245],
      textColor: navy,
      fontStyle: "bold",
      fontSize: 11,
    },
    columnStyles: {
      0: { cellWidth: 15, halign: "center" },
      1: { cellWidth: 112 },
      2: { cellWidth: 20, halign: "center" },
      3: { cellWidth: 39, halign: "right" },
    },
  });

  let y = doc.lastAutoTable.finalY + 8;

  // Amount in words
  autoTable(doc, {
    startY: y,
    margin: { left: 12, right: 12 },
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: 3,
      lineColor: [190, 200, 215],
      lineWidth: 0.2,
    },
    body: [
      [
        {
          content: `Amount in Words: Rupees ${words(fee.grandTotal)} Only`,
          styles: { fontStyle: "bold", textColor: navy },
        },
      ],
    ],
  });

  y = doc.lastAutoTable.finalY + 8;

  // Bank + Tax tables
  autoTable(doc, {
    startY: y,
    margin: { left: 12, right: 12 },
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 8.5,
      cellPadding: 2.5,
      lineColor: [190, 200, 215],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: navy,
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    head: [["Bank Details", "GST Summary"]],
    body: [
      [
        `Bank Name: ${COMPANY.bank}\nA/C Name: ${COMPANY.accountName}\nA/C No: ${COMPANY.accountNo}\nIFSC: ${COMPANY.ifsc}`,
        `Taxable Value: ${inr(fee.consultancyFee)}\nCGST 9%: ${inr(fee.cgst)}\nSGST 9%: ${inr(fee.sgst)}\nTotal GST: ${inr(fee.totalGst)}`,
      ],
    ],
    columnStyles: {
      0: { cellWidth: 93 },
      1: { cellWidth: 93 },
    },
  });

  // Terms
  y = doc.lastAutoTable.finalY + 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...navy);
  doc.text("Terms & Notes", 14, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(60, 60, 60);
  doc.text("1. GST @18% is charged extra on consultancy fee.", 14, y + 6);
  doc.text("2. Payment received through RTGS / NEFT / Bank Transfer only.", 14, y + 11);
  doc.text("3. This is a computer generated invoice.", 14, y + 16);

  // Signature block
  doc.addImage(signature, "PNG", 130, 242, 58, 25);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(0, 0, 0);
  doc.text("For Shine Agri Allied and Marketing MSCS Ltd.", 122, 274);
  doc.text("Authorized Signatory", 150, 284);

  // Footer
  doc.setDrawColor(...navy);
  doc.line(12, 286, 198, 286);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(100, 100, 100);
  doc.text("Generated by Shine Agri Office Scheme Automation System", 105, 291, {
    align: "center",
  });

  doc.save(`GST_Invoice_${application.societyName || "PACS"}.pdf`);
}